/**
 * Anmeldung des Service Workers und Umgang mit neuen Fassungen.
 *
 * Der Vorgänger hatte hier den entscheidenden Fehler: der Worker übernahm
 * ungefragt und hielt gleichzeitig eine alte Oberfläche fest. Deshalb hier:
 *
 *  - Der neue Worker wartet, bis der Benutzer zustimmt. Mitten in einer
 *    Prüfung soll sich nichts neu laden.
 *  - Beim Zustimmen wird `SKIP_WAITING` geschickt und erst nach
 *    `controllerchange` neu geladen. Ohne diese Reihenfolge lädt die Seite
 *    neu, bevor der neue Worker zuständig ist – und die alte Fassung kommt
 *    zurück.
 *  - Nur ein einziges Neuladen, gesichert über eine Sperre.
 */

import { registerSW } from 'virtual:pwa-register'

type Rueckmeldung = () => void

// Mehrere Zuhörer und ein Merker, ob die Meldung schon kam.
//
// Vorher war es eine einzelne Rückmeldung, die erst der Rahmen gesetzt hat.
// Kam `onNeedRefresh`, bevor der Rahmen stand – oder auf der Anmeldeseite,
// wo es ihn gar nicht gibt –, ging die Meldung ins Leere und kam nie wieder.
// Wer abgemeldet war, blieb damit dauerhaft auf der alten Fassung.
const zuhoerer = new Set<Rueckmeldung>()
let stehtBereit = false
let uebernehmen: ((neuladen?: boolean) => Promise<void>) | undefined
let schonNeugeladen = false

export function serviceWorkerAnmelden(): void {
  // In der Entwicklung gibt es keinen Worker; registerSW ist dann wirkungslos.
  uebernehmen = registerSW({
    immediate: true,
    onNeedRefresh() {
      stehtBereit = true
      for (const fn of zuhoerer) fn()
    },
    onRegisteredSW(_pfad, anmeldung) {
      // Einmal pro Stunde nach einer neuen Fassung sehen. Ein Prüfer hat die
      // App wochenlang offen, ohne sie zu schließen.
      if (!anmeldung) return
      window.setInterval(() => void anmeldung.update(), 60 * 60 * 1000)
    },
    onRegisterError(fehler) {
      // Kein Grund, die App anzuhalten – sie läuft dann eben ohne Offlinehülle.
      console.warn('Service Worker konnte nicht angemeldet werden', fehler)
    },
  })
}

/**
 * Meldet sich, sobald eine neue Fassung bereitsteht.
 *
 * Stand sie schon vor dem Anmelden bereit, kommt die Meldung sofort – sonst
 * verpasst jeder Zuhörer, der später dazukommt, den einen Zeitpunkt.
 */
export function neueFassungVerfuegbar(fn: Rueckmeldung): () => void {
  zuhoerer.add(fn)
  if (stehtBereit) fn()
  return () => {
    zuhoerer.delete(fn)
  }
}

/**
 * Übernimmt die neue Fassung.
 *
 * Das Neuladen erledigen wir selbst und überlassen es nicht `registerSW`.
 * Dessen Reload hängt an `event.isUpdate`, und das ist false, wenn die Seite
 * ohne zuständigen Worker geladen wurde – etwa direkt nach dem Löschen der
 * Websitedaten oder beim allerersten Besuch. In dem Fall übernahm der neue
 * Worker zwar, die Seite lud aber nie neu: der Knopf tat sichtbar nichts.
 *
 * Reihenfolge wie gehabt: erst zuhören, dann SKIP_WAITING schicken, erst
 * nach `controllerchange` neu laden. Andersherum käme die alte Fassung
 * zurück, weil beim Neuladen noch der alte Worker zuständig wäre.
 *
 * Die Sperre verhindert eine Schleife, falls jemand zweimal tippt.
 */
export async function neueFassungLaden(): Promise<void> {
  if (schonNeugeladen) return
  schonNeugeladen = true

  navigator.serviceWorker?.addEventListener(
    'controllerchange',
    () => window.location.reload(),
    { once: true },
  )

  await uebernehmen?.(false)

  // Notnagel, wenn der Worker nicht übernimmt.
  //
  // Ein einfaches Neuladen genügt hier nicht: der alte Worker kann dabei
  // erneut die alte Fassung ausliefern, und dann sieht es wieder so aus, als
  // täte der Knopf nichts. Also abmelden, Dateispeicher leeren, neu laden.
  //
  // Das ist ungefährlich. Der Dateispeicher enthält nur die App selbst und
  // Fotos, die auch auf dem Server liegen. Die Warteschlange mit den noch
  // nicht gesendeten Änderungen steckt in IndexedDB und bleibt unberührt,
  // ebenso die Anmeldung.
  window.setTimeout(async () => {
    try {
      for (const anmeldung of await navigator.serviceWorker.getRegistrations()) {
        await anmeldung.unregister()
      }
      for (const name of await caches.keys()) await caches.delete(name)
    } catch (fehler) {
      console.warn('Aufräumen vor dem Neuladen gescheitert', fehler)
    }
    window.location.reload()
  }, 3000)
}
